import { supabase } from './supabase';
import { useLibrary } from './libraryStore';
import type { ComponentTemplate } from './types';

/**
 * Automatic cloud sync for the component library (`libraries` table — one row
 * per user, owner-only via RLS). No buttons: App starts the sync when a
 * session exists.
 *
 *   • On start: pull the cloud library and MERGE it with local (union by key;
 *     on conflict the local copy wins — it's what the user sees and edits).
 *     The merged result is saved locally and pushed back up.
 *   • After that: every local library change is pushed (debounced), so the
 *     library follows the account across devices.
 *
 * File-based sharing stays as-is: Palette's export/import (with the merge
 * preview modal) is for exchanging libraries between people; this sync is for
 * one person's devices.
 */

/** Hard cap on the serialized library, mirroring the project-size guard. The
 *  library is text-only templates, so this is generous. */
const MAX_LIBRARY_BYTES = 2_000_000;

function fingerprint(templates: ComponentTemplate[]): string {
  return JSON.stringify([...templates].sort((a, b) => a.key.localeCompare(b.key)));
}

/** Union by key; local wins when both sides have the same key. */
function mergeForSync(
  local: ComponentTemplate[],
  cloud: ComponentTemplate[],
): ComponentTemplate[] {
  const byKey = new Map<string, ComponentTemplate>();
  for (const t of cloud) byKey.set(t.key, t);
  for (const t of local) byKey.set(t.key, t);
  return Array.from(byKey.values());
}

async function pull(): Promise<ComponentTemplate[] | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.from('libraries').select('data').maybeSingle();
  if (error) throw error;
  return Array.isArray(data?.data) ? (data.data as ComponentTemplate[]) : null;
}

async function push(templates: ComponentTemplate[]): Promise<void> {
  if (!supabase) return;
  const json = JSON.stringify(templates);
  if (json.length > MAX_LIBRARY_BYTES) return; // silently skip pathological sizes
  const { data: user } = await supabase.auth.getUser();
  const uid = user.user?.id;
  if (!uid) return;
  const { error } = await supabase
    .from('libraries')
    .upsert({ user_id: uid, data: templates, updated_at: new Date().toISOString() });
  if (error) throw error;
}

/**
 * Start the sync (call when a session exists). Returns a stop function.
 * All failures are silent — the library keeps working locally offline.
 */
export function startLibraryCloudSync(): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Set while we apply the merged result locally, so our own setAll doesn't
  // immediately re-trigger a push (it would be a no-op write anyway).
  let applying = false;
  let lastPushed = '';

  const schedulePush = (templates: ComponentTemplate[]) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      const fp = fingerprint(templates);
      if (fp === lastPushed) return;
      void push(templates)
        .then(() => {
          lastPushed = fp;
        })
        .catch(() => {
          // offline / table missing — retry on the next local change
        });
    }, 2000);
  };

  // Initial pull + merge.
  void pull()
    .then((cloud) => {
      if (stopped) return;
      const local = useLibrary.getState().templates;
      if (cloud && cloud.length > 0) {
        const merged = mergeForSync(local, cloud);
        if (fingerprint(merged) !== fingerprint(local)) {
          applying = true;
          useLibrary.getState().setAll(merged);
          applying = false;
        }
        lastPushed = fingerprint(cloud);
        if (fingerprint(merged) !== lastPushed) schedulePush(merged);
      } else if (local.length > 0) {
        // Nothing in the cloud yet — seed it with the local library.
        schedulePush(local);
      }
    })
    .catch(() => {
      // offline / table missing — sync silently does nothing
    });

  // Follow local edits.
  const unsub = useLibrary.subscribe((state) => {
    if (stopped || applying) return;
    schedulePush(state.templates);
  });

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    unsub();
  };
}
